/**
 * Fase 28 · Helper unificado para URLs de assets + componente SafeImage.
 *
 * assetUrl(fileUrl):
 *  - Si `fileUrl` es absoluta (http/https/data:) → devuelve tal cual.
 *  - Si es relativa (`/api/...`) → prefija con el host del backend (sin duplicar
 *    dominios) para evitar el bug de "double domain" reportado en fase 27.1.
 *  - Vacío/null → devuelve "".
 *
 * SafeImage: <img> con fallback visual "Archivo no disponible — regenerar"
 *   cuando el archivo no puede cargarse (404, ACL, red, etc.). Se usa en Motor
 *   Creativo, Galería, GalleryPicker y cualquier lugar donde una pieza pueda
 *   tener el archivo perdido en storage.
 */
import React, { useState } from "react";
import { API_BASE } from "./api";
import { AlertTriangle } from "lucide-react";

export function assetUrl(fileUrl) {
  if (!fileUrl) return "";
  if (/^(https?:|data:)/i.test(fileUrl)) return fileUrl;
  const base = API_BASE.replace(/\/api$/, "");
  return `${base}${fileUrl.startsWith("/") ? "" : "/"}${fileUrl}`;
}

/**
 * SafeImage — muestra la imagen. Si onError, reemplaza por placeholder.
 * Props extra: `fallbackLabel` (texto), `fallbackHint` (subtítulo).
 */
export function SafeImage({
  src,
  alt = "",
  className = "",
  fallbackLabel = "Archivo no disponible",
  fallbackHint = "Regenerar desde el Motor Creativo",
  testid,
  onErrorCallback,
  ...rest
}) {
  const [errored, setErrored] = useState(false);
  const resolvedSrc = assetUrl(src);
  const hasSrc = Boolean(resolvedSrc);

  if (!hasSrc || errored) {
    return (
      <div
        className={`flex flex-col items-center justify-center bg-stone-100 border border-dashed border-stone-300 text-center p-4 ${className}`}
        data-testid={testid ? `${testid}-broken` : "safeimage-broken"}
      >
        <AlertTriangle className="h-6 w-6 text-brand-gold-dark mb-1.5" strokeWidth={1.6} />
        <div className="text-xs font-semibold text-brand-ink">{fallbackLabel}</div>
        <div className="text-[10px] text-stone-500 mt-0.5 max-w-[180px]">{fallbackHint}</div>
      </div>
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      className={className}
      data-testid={testid}
      onError={() => {
        setErrored(true);
        if (onErrorCallback) onErrorCallback();
      }}
      {...rest}
    />
  );
}
